<template>
    <v-card outlined v-bind="{ loading }">
        <v-card-title>
            <v-row no-gutters align="center">
                <v-col cols="auto">
                    <span> {{ modeText }} 매출 </span>
                    <span v-if="mode == NSHOP_STATISTICS_MODES.WEEKLY.value && summary.title" class="ml-2 subtitle-1 grey--text">({{ summary.title }})</span>
                </v-col>
                <v-spacer />
                <v-col cols="auto" class="my-n2">
                    <v-date-field v-model="date" :type="mode == NSHOP_STATISTICS_MODES.MONTHLY.value ? 'month' : 'date'" :label="dateLabel" persistent-placeholder hide-details @input="search" />
                </v-col>
            </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text>
            <div v-if="!searched && idleText" class="mb-3 grey--text">{{ idleText }}</div>
            <v-row align="center">
                <template v-for="item in items">
                    <v-col :key="item.title" cols="6" sm="4" md="3" class="text-center">
                        <v-card outlined>
                            <v-row class="flex-column ma-0">
                                <v-col class="text-h6">{{ item.title }}</v-col>
                                <v-divider />
                                <v-col class="text-h6 font-weight-bold">{{ item.value }}</v-col>
                            </v-row>
                        </v-card>
                    </v-col>
                </template>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script>
import api from "@/api";
import VDateField from "@/components/plugins/vuetify/v-date-field.vue";
import { NSHOP_STATISTICS_MODES, OPENBANK_PG_TEXTS } from "@/store/nshop";

export default {
    components: {
        VDateField,
    },
    props: {
        mode: { type: String, default: NSHOP_STATISTICS_MODES.DAILY.value }, // NSHOP_STATISTICS_MODES 값
        idleText: { type: String, default: null }, // 아직 한 번도 조회하지 않았을 때 보일 안내
    },
    data() {
        return {
            NSHOP_STATISTICS_MODES,

            // 월간은 v-date-field type="month" 라 YYYY-MM, 나머지는 YYYY-MM-DD
            date: this.mode == NSHOP_STATISTICS_MODES.MONTHLY.value ? this.$dayjs().format("YYYY-MM") : this.$dayjs().format("YYYY-MM-DD"),

            loading: false,

            // 한 번이라도 조회를 시작했는지. 페이지가 아직 부르지 않은 묶음을 가린다.
            searched: false,

            // 요청 번호. 불러오는 중에 날짜를 바꾸면 새 요청을 보내고, 늦게 온 옛 응답은 버린다.
            requestId: 0,

            // 서버 응답 그대로. 읽는 곳은 아래 computed summary 한 곳이다. 조회가 실패하면 null 로 비운다.
            response: null,
        };
    },
    computed: {
        modeText() {
            return NSHOP_STATISTICS_MODES[this.mode]?.text;
        },
        dateLabel() {
            // 주간은 고른 날이 든 주(일요일 시작)를 서버가 정한다
            return { DAILY: "매출일", WEEKLY: "매출주(기준일)", MONTHLY: "매출월" }[this.mode];
        },
        // 응답 키를 읽는 유일한 곳. 응답 모양이 바뀌면 여기만 고친다.
        summary() {
            const { title, daehanbooks, paysolus } = this.response || {};
            return {
                loaded: !!this.response,
                title,
                daehanbooksTotal: daehanbooks?.total,
                checkpayTotal: daehanbooks?.checkpay,
                wpayTotal: daehanbooks?.wpay,
                // 나누리핀 호출이 실패하면 null — 0 원과 구별한다
                paysolusTotal: paysolus ? paysolus.total : null,
            };
        },
        items() {
            const { loaded, daehanbooksTotal, checkpayTotal, wpayTotal, paysolusTotal } = this.summary;
            return [
                { title: "나누리상품권 매출", value: daehanbooksTotal?.format?.() },
                { title: OPENBANK_PG_TEXTS.checkpay, value: checkpayTotal?.format?.() },
                { title: OPENBANK_PG_TEXTS.wpay, value: wpayTotal?.format?.() },
                { title: "페이솔루스", value: loaded && paysolusTotal === null ? "불러오지 못했습니다" : paysolusTotal?.format?.() },
            ];
        },
    },
    methods: {
        /**
         * 고른 날짜로 조회한다. 마지막 요청의 결과만 화면에 남는다.
         * 첫 조회는 페이지(sales-dashboard/index.vue)가 순서를 정해 부른다.
         * 마지막 요청이 성공하면 success 이벤트를 올린다.
         * @returns {Promise<boolean>} 이 요청의 성공 여부
         */
        async search() {
            const requestId = ++this.requestId;
            this.searched = true;
            this.loading = true;
            try {
                const { mode } = this;
                // 월간은 그 달 1일을 보낸다
                const date = mode == NSHOP_STATISTICS_MODES.MONTHLY.value ? `${this.date}-01` : this.date;
                const response = await api.integratedViewer.salesDashboard.get({
                    params: { mode, date },
                });
                if (requestId == this.requestId) {
                    this.response = response;
                    this.$emit("success");
                }
                return true;
            } catch (error) {
                console.error(error);
                if (requestId == this.requestId) {
                    // 이전 날짜의 숫자가 남지 않게 비운다
                    this.response = null;
                    // 403 은 axios 인터셉터(src/plugins/axios.js)가 이미 alert 를 띄웠다. 그 밖(5xx·401·네트워크)은 여기서 알린다.
                    if (error.response?.status != 403) alert(error.response?.data?.message || error.message);
                }
                return false;
            } finally {
                if (requestId == this.requestId) this.loading = false;
            }
        },
    },
};
</script>
